import './QuestionForm.css';
import { useState, useEffect } from 'react';
import { searchBooks } from '../../../services/bookService';
import { searchAuthors } from '../../../services/authorService';

function FillInForm({ onSubmit, onCancel, initialData, isEdit }) {
    const [questionText, setQuestionText] = useState(initialData?.questionText || '');
    const [text, setText] = useState(initialData?.text || '');
    const [words, setWords] = useState([]);
    const [blankIndices, setBlankIndices] = useState(initialData?.blankIndices || []);

    // Combined search for books and authors
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchResults, setSearchResults] = useState({ books: [], authors: [] });
    const [selectedItems, setSelectedItems] = useState(initialData?.selectedItems || []);
    const [searching, setSearching] = useState(false);

    // Parse text into words whenever text changes
    useEffect(() => {
        if (text.trim()) {
            const parsedWords = text.trim().split(/\s+/);
            setWords(parsedWords);
            // Remove any indices that are out of bounds
            setBlankIndices(prev => prev.filter(i => i < parsedWords.length));
        } else {
            setWords([]);
            setBlankIndices([]);
        }
    }, [text]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search both books and authors
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearch.trim().length < 2) {
                setSearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }

            try {
                setSearching(true);

                const term = debouncedSearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());

                setSearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setSearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearch]);

    const stripPunctuation = (word) => {
        if (!word) return '';
        return word.replace(/[.,!?;:"""''„"«»()[\]{}]/g, '');
    };

    const handleWordClick = (index) => {
        setBlankIndices(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleSelectBook = (book) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) {
            alert('Tato kniha je již vybrána');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'book', data: book }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectAuthor = (author) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) {
            alert('Tento autor je již vybrán');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'author', data: author }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedItems.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedItems([...selectedItems, { type: 'unlinked' }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleRemoveSelected = (index) => {
        setSelectedItems(selectedItems.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!questionText.trim()) {
            alert('Zadejte název otázky');
            return;
        }

        if (!text.trim()) {
            alert('Zadejte text');
            return;
        }

        if (blankIndices.length === 0) {
            alert('Označte alespoň jedno slovo k doplnění');
            return;
        }

        const sortedIndices = [...blankIndices].sort((a, b) => a - b);
        const correctAnswers = sortedIndices.map(i => stripPunctuation(words[i]));

        const questionData = {
            type: 'FillIn',
            questionText: questionText.trim(),
            text: text.trim(),
            blankPosition: sortedIndices[0],
            correctAnswers: correctAnswers,
            relatedBooks: selectedItems.filter(item => item.type === 'book').map(item => item.data.id),
            relatedAuthors: selectedItems.filter(item => item.type === 'author').map(item => item.data.id)
        };

        onSubmit(questionData);
    };

    return (
        <form className="question-form" onSubmit={handleSubmit}>
            {/* Question Title */}
            <div className="form-group">
                <label htmlFor="questionText" className="form-label required">
                    Název otázky
                </label>
                <input
                    type="text"
                    id="questionText"
                    className="form-input"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Zadejte název otázky..."
                    required
                />
            </div>

            {/* Full text */}
            <div className="form-group">
                <label htmlFor="text" className="form-label required">
                    Celý text
                </label>
                <textarea
                    id="text"
                    className="form-textarea"
                    rows="4"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Napište celý text včetně slov, která má uživatel doplnit..."
                    required
                />
                <p className="form-hint">
                    Napište kompletní text. Poté níže klikněte na slova, která se mají skrýt a uživatel je bude doplňovat.
                </p>
            </div>

            {/* Word Selection */}
            {words.length > 0 && (
                <div className="form-group">
                    <label className="form-label required">Označte slova k doplnění</label>
                    <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#0f3460',
                        border: '2px solid #1a1a2e',
                        borderRadius: '6px',
                        lineHeight: '2.5',
                        fontSize: '1.1rem'
                    }}>
                        {words.map((word, index) => (
                            <span
                                key={index}
                                onClick={() => handleWordClick(index)}
                                style={{
                                    padding: '0.4rem 0.6rem',
                                    margin: '0.2rem',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    backgroundColor: blankIndices.includes(index) ? '#e94560' : 'transparent',
                                    color: blankIndices.includes(index) ? 'white' : '#eee',
                                    border: blankIndices.includes(index) ? '2px solid #e94560' : '2px solid transparent',
                                    transition: 'all 0.2s ease',
                                    display: 'inline-block'
                                }}
                                onMouseEnter={(e) => {
                                    if (!blankIndices.includes(index)) {
                                        e.target.style.backgroundColor = 'rgba(233, 69, 96, 0.2)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!blankIndices.includes(index)) {
                                        e.target.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                {word}
                            </span>
                        ))}
                    </div>
                    {blankIndices.length > 0 && (
                        <p className="form-hint" style={{ marginTop: '1rem', color: '#e94560' }}>
                            Slova k doplnění: <strong>{[...blankIndices].sort((a, b) => a - b).map(i => stripPunctuation(words[i])).join(', ')}</strong>
                        </p>
                    )}
                </div>
            )}

            {/* Related Books and Authors */}
            <div className="form-group">
                <label className="form-label">Související knihy a autoři (nepovinné)</label>

                {selectedItems.length > 0 && (
                    <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {selectedItems.map((item, index) => (
                            <div key={index} className="selected-item">
                                <div className="selected-item-info">
                                    <span className="selected-item-type">
                                        {item.type === 'book' ? 'Kniha' : item.type === 'unlinked' ? 'Kategorie' : 'Autor'}
                                    </span>
                                    <span className="selected-item-title">
                                        {item.type === 'book'
                                            ? item.data.bookTitle
                                            : item.type === 'unlinked'
                                            ? 'Literární teorie'
                                            : item.data.name}
                                    </span>
                                    {item.type === 'book' && item.data.author?.name && (
                                        <span className="selected-item-subtitle">
                                            {item.data.author.name}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-remove-selected"
                                    onClick={() => handleRemoveSelected(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    type="text"
                    className="form-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Vyhledat knihu nebo autora..."
                />
                {searching && <p className="search-loading">Hledám...</p>}
                {(searchResults.books.length > 0 || searchResults.authors.length > 0 || searchResults.showUnlinked) && (
                    <div className="search-results">
                        {searchResults.showUnlinked && (
                            <div
                                className="search-result-item"
                                onClick={handleSelectUnlinked}
                            >
                                <span className="result-title">Literární teorie</span>
                                <span className="result-subtitle">Otázky o literární teorii</span>
                            </div>
                        )}
                        {searchResults.books.length > 0 && (
                            <>
                                <div className="search-results-section-header">Knihy</div>
                                {searchResults.books.map(book => (
                                    <div
                                        key={`book-${book.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectBook(book)}
                                    >
                                        <span className="result-title">{book.bookTitle}</span>
                                        <span className="result-subtitle">{book.author?.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                        {searchResults.authors.length > 0 && (
                            <>
                                <div className="search-results-section-header">Autoři</div>
                                {searchResults.authors.map(author => (
                                    <div
                                        key={`author-${author.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectAuthor(author)}
                                    >
                                        <span className="result-title">{author.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button type="submit" className="btn-submit">
                    {isEdit ? 'Uložit změny' : 'Vytvořit otázku'}
                </button>
                <button type="button" className="btn-cancel-form" onClick={onCancel}>
                    Zrušit
                </button>
            </div>
        </form>
    );
}

export default FillInForm;
